import {
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Control,
  UseFormRegister,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CreateMaterialRequest,
  Material,
  MixedMaterialComponent,
} from '@texas/api/endpoints/metadata/materialsApi';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { formatLabel } from '@texas/components/shared/composition/material/shared';
import { MaterialRecommendations } from './MaterialRecommendations';
import {
  hasValidMaterialPercentages,
  materialPercentageTotal,
} from './mixedMaterial';

interface Props {
  control: Control<CreateMaterialRequest>;
  register: UseFormRegister<CreateMaterialRequest>;
  materials: Material[] | null | undefined;
  isLoading: boolean;
  materialId?: number;
  existingComponents?: MixedMaterialComponent[];
}

export function MixedMaterialFields({
  control,
  register,
  materials,
  isLoading,
  materialId,
  existingComponents = [],
}: Props) {
  const { t } = useTranslation();
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'childMaterials',
    rules: {
      validate: (components) =>
        hasValidMaterialPercentages(components) ||
        t('configuration.mixedPercentagesRequired'),
    },
  });
  const components = useWatch({
    control,
    name: 'childMaterials',
    defaultValue: [],
  });
  const options = (materials ?? [])
    .filter((material) => material.id !== materialId)
    .map((material) => ({ value: material.id, label: formatLabel(material) }));
  const materialLabel = (id: number) =>
    options.find((option) => option.value === id)?.label ??
    existingComponents.find((existing) => existing.id === id)?.name ??
    String(id);
  const selectedOptions = components.map((component) => ({
    value: component.materialId,
    label: materialLabel(component.materialId),
  }));
  const isValid = hasValidMaterialPercentages(components);
  const total = materialPercentageTotal(components);

  return (
    <FormControl isInvalid={!isValid}>
      <TexasFormLabel>{t('configuration.combinationOf')}</TexasFormLabel>
      <TexasSelect
        name="childMaterials"
        isLoading={isLoading}
        isClearable={true}
        isMulti={true}
        usePortal={true}
        value={selectedOptions}
        options={options}
        onChange={(selected) =>
          replace(
            selected.map((option) => ({
              materialId: option.value,
              percentage:
                components.find(
                  (component) => component.materialId === option.value,
                )?.percentage ?? 0,
            })),
          )
        }
        closeMenuOnSelect={false}
      />
      <Text variant="small" pt={1}>
        {t('configuration.mixedDesc')}
      </Text>
      {materialId && !isLoading && (
        <MaterialRecommendations
          materialId={materialId}
          selectedMaterialIds={components.map(
            (component) => component.materialId,
          )}
          onAdd={(recommendedId) => {
            if (
              components.some(
                (component) => component.materialId === recommendedId,
              )
            )
              return;
            append(
              { materialId: recommendedId, percentage: 0 },
              { shouldFocus: false },
            );
          }}
        />
      )}
      {components.length > 0 && (
        <VStack
          align="stretch"
          spacing={3}
          mt={4}
          pt={4}
          borderTopWidth="1px"
          borderColor="entityDirectory.border"
        >
          {fields.map((component, index) => (
            <Flex key={component.id} align="center" gap={3}>
              <Text
                flex={1}
                minW={0}
                fontSize="sm"
                id={`mixed-material-${component.materialId}`}
              >
                {materialLabel(component.materialId)}
              </Text>
              <InputGroup w="130px" flexShrink={0} size="sm">
                <Input
                  aria-labelledby={`mixed-material-${component.materialId}`}
                  variant="entityDirectory"
                  type="number"
                  min={0.01}
                  max={100}
                  step={0.01}
                  {...register(`childMaterials.${index}.percentage`, {
                    valueAsNumber: true,
                    required: true,
                    min: 0.01,
                    max: 100,
                  })}
                />
                <InputRightAddon>%</InputRightAddon>
              </InputGroup>
            </Flex>
          ))}
          <Text
            color={isValid ? 'status.success' : 'status.danger'}
            fontWeight="semibold"
          >
            {t('configuration.mixedPercentageTotal', {
              total: Number.isFinite(total) ? Number(total.toFixed(2)) : '–',
            })}
          </Text>
          {!isValid && (
            <Text fontSize="sm" color="status.danger">
              {t('configuration.mixedPercentagesRequired')}
            </Text>
          )}
        </VStack>
      )}
    </FormControl>
  );
}
